import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isOpenKnotsEnabled } from '../../../config/openKnotsFeature';
import { getOpsDashboardTabFromSearch } from '../../../utils/routeHistory';

export const isTruthyEnvFlag = (value) => String(value) === 'true';

export const getPlatformRole = (userData) =>
    userData?.user_organizations?.[0]?.user_org_role_maps?.[0]?.platform_role;

export const getRoleRelationship = (userData) =>
    userData?.user_organizations?.[0]?.user_org_role_maps?.[0]?.role_relationship;

export const getDashboardPath = (userData) => {
    const role = getPlatformRole(userData);
    if ((role === 'buyer' || role === 'supplier') && isOpenKnotsEnabled(userData)) {
        return '/dashboard?type=openKnots';
    }
    return '/dashboard';
};

const pageClass = (isActive) => (isActive ? 'page-active' : 'page-inactive');

export const collapsedNavAttrs = (isBarExpanded, label) => ({
    'data-label': label,
    'aria-label': isBarExpanded ? undefined : label,
});

const onNav = (ctx, props, page) => {
    ctx.onPageClick(page);
    props.hideSidebar();
};

export const showTradeSimulator = (userData, knotTabs) => {
    if (!userData) {
        return false;
    }
    const role = getPlatformRole(userData);
    if (role !== 'buyer' && role !== 'supplier') {
        return false;
    }
    return knotTabs.isVisible('trade');
};

export const showBuyerSupplierAccount = (userData) => {
    if (!userData) {
        return false;
    }
    const role = getPlatformRole(userData);
    return role === 'buyer' || role === 'supplier' || role === 'broker';
};

const showMyRequestsInAccount = (platformRole) =>
    platformRole === 'broker' ||
    platformRole === 'internal_ops' ||
    platformRole === 'admin' ||
    platformRole === 'operator';

export const showInternalOpsNav = (userData) => {
    if (!userData) {
        return false;
    }
    const role = getPlatformRole(userData);
    return role === 'internal_ops' || role === 'admin';
};

export const showPricingConfigurator = (userData) => {
    if (!userData) {
        return false;
    }
    const rel = getRoleRelationship(userData);
    return rel === 'admin' || rel === 'operator' || rel === 'credit_analyst';
};

export const showOpsMyAccount = (showAssignmentFlag) => isTruthyEnvFlag(showAssignmentFlag);

export const OPS_MY_ACCOUNT_PARENT_PATH = '/userAssignment';

export const shouldToggleOpsAccountSubmenu = (isBarExpanded) => Boolean(isBarExpanded);

const useAccountSubmenu = (isBarExpanded) => {
    const [isAccountOpen, setIsAccountOpen] = useState(false);
    const isExpanded = shouldToggleOpsAccountSubmenu(isBarExpanded);

    useEffect(() => {
        if (!isExpanded) {
            setIsAccountOpen(false);
        }
    }, [isExpanded]);

    return { isAccountOpen, setIsAccountOpen, isExpanded };
};

export const getOpsSidebarActiveKey = (pathname, search) => {
    if (pathname === '/dashboard') {
        const tab = getOpsDashboardTabFromSearch(search);
        if (tab === 'transactions') {
            return 'allTransactions';
        }
        if (tab === 'requests') {
            return 'allRequests';
        }
        if (tab === 'rfqs') {
            return 'allRfqs';
        }
        return 'dashboard';
    }
    if (pathname.startsWith('/manageProfiles')) {
        return 'profile';
    }
    if (pathname.startsWith('/facilities') || pathname === '/allFacilities') {
        return 'allFacilities';
    }
    if (pathname.startsWith('/pricingConfigurator')) {
        return 'pricingConfigurator';
    }
    if (pathname.startsWith('/notifications') || pathname.startsWith('/userAssignment')) {
        return 'myAccount';
    }
    return null;
};

export const SidebarHomeLink = ({ ctx, props }) => {
    const location = useLocation();
    const isActive = getOpsSidebarActiveKey(location.pathname, location.search) === 'dashboard';
    return (
        <Link to={getDashboardPath(ctx.userData)}>
            <p
                className={`sidebar-nav-item pt-3 pb-2 font-b2 cursor mb-0 border-bottom ${pageClass(isActive)}`}
                {...collapsedNavAttrs(props.isBarExpanded, 'Home')}
                onClick={() => onNav(ctx, props, 'dashboard')}>
                <span className='sf-home home-icon'></span>
                <span className={`align-top  font-medium ${props.isBarExpanded ? 'd-inline' : 'd-none'}`}>Home</span>
            </p>
        </Link>
    );
};

export const SidebarTradeSimulator = ({ ctx, props, knotTabs }) => {
    if (!showTradeSimulator(ctx.userData, knotTabs)) {
        return null;
    }
    return (
        <Link to='/priceSimulator'>
            <p
                className={`sidebar-nav-item pt-3 pb-2 font-b2 cursor mb-0 border-bottom ${pageClass(
                    ctx.pageActive === 'priceSimulator'
                )}`}
                {...collapsedNavAttrs(props.isBarExpanded, 'Trade support Simulator')}
                onClick={() => onNav(ctx, props, 'priceSimulator')}>
                <span className='sf-simulator home-icon'></span>
                <span className={`align-top font-medium ${props.isBarExpanded ? 'd-inline' : 'd-none'}`}>
                    Trade support Simulator
                </span>
            </p>
        </Link>
    );
};

export const SidebarBuyerSupplierAccount = ({ ctx, props, showAssignment }) => {
    const { isAccountOpen, setIsAccountOpen, isExpanded } = useAccountSubmenu(props.isBarExpanded);
    if (!showBuyerSupplierAccount(ctx.userData)) {
        return null;
    }
    const platformRole = getPlatformRole(ctx.userData);
    const parentRow = (
        <p
            className={`sidebar-nav-item pt-3 pb-2 font-b2 mb-0 cursor border-top-gray ${pageClass(
                ctx.pageActive === 'myProfile' || ctx.pageActive === 'myRequests'
            )}`}
            {...collapsedNavAttrs(props.isBarExpanded, 'My Account')}
            role={isExpanded ? 'button' : undefined}
            aria-expanded={isExpanded ? isAccountOpen : undefined}
            onClick={isExpanded ? () => setIsAccountOpen((open) => !open) : undefined}>
            <span className='sf-user-profile home-icon'></span>
            <span className={props.isBarExpanded ? 'd-inline align-top' : 'd-none'}>My Account</span>
            {isExpanded && <span className='sf-chevron-down float-end me-3 mt-1 pt-1'></span>}
        </p>
    );
    return (
        <>
            {isExpanded ? (
                parentRow
            ) : (
                <Link to='/myProfile' onClick={() => onNav(ctx, props, 'myProfile')}>
                    {parentRow}
                </Link>
            )}
            {isExpanded && isAccountOpen && (
                <div>
                    {showMyRequestsInAccount(platformRole) && (
                        <Link to={`/requests`}>
                            <p
                                className='font-b2 ms-4 pb-2 cursor mt-2 border-bottom mb-2'
                                onClick={() => onNav(ctx, props, 'myRequests')}>
                                <i className='sf-chevron-right me-3'></i> My Requests
                            </p>
                        </Link>
                    )}
                    <Link to='/myProfile'>
                        <p
                            className={`font-b2 ms-4 pb-2 cursor  mb-2 ${showAssignment && 'border-bottom'} `}
                            onClick={() => onNav(ctx, props, 'myProfile')}>
                            <i className='sf-chevron-right me-3'></i> My Profile
                        </p>
                    </Link>
                    {showAssignment && (
                        <Link to='/userAssignment'>
                            <p
                                className='font-b2 ms-4 cursor '
                                onClick={() => onNav(ctx, props, 'userAssignment')}>
                                <i className='sf-chevron-right me-3'></i>User Assignment
                            </p>
                        </Link>
                    )}
                </div>
            )}
            <hr className='mt-0' />
        </>
    );
};

const OpsNavLink = ({ to, page, iconClass, label, isActive, ctx, props }) => (
    <Link to={to}>
        <p
            className={`sidebar-nav-item pt-2 pb-2 font-b2 cursor mb-0 border-bottom ${pageClass(isActive)}`}
            {...collapsedNavAttrs(props.isBarExpanded, label)}
            onClick={() => onNav(ctx, props, page)}>
            <span className={`${iconClass} home-icon`}></span>
            <span className={`align-top ${props.isBarExpanded ? 'd-inline' : 'd-none'}`}>{label}</span>
        </p>
    </Link>
);

const OpsAccountSection = ({ ctx, props, isActive }) => {
    const { isAccountOpen, setIsAccountOpen, isExpanded } = useAccountSubmenu(props.isBarExpanded);

    const parentRow = (
        <p
            className={`sidebar-nav-item pt-2 pb-2 font-b2 mb-0 cursor border-top-gray mt-2 ${pageClass(isActive)}`}
            {...collapsedNavAttrs(props.isBarExpanded, 'My Account')}
            role={isExpanded ? 'button' : undefined}
            aria-expanded={isExpanded ? isAccountOpen : undefined}
            onClick={
                isExpanded
                    ? () => setIsAccountOpen((open) => !open)
                    : undefined
            }>
            <span className='sf-user home-icon'></span>
            <span className={props.isBarExpanded ? 'd-inline align-top' : 'd-none'}>My Account</span>
            {isExpanded && <span className='sf-chevron-down float-end me-3 mt-1 pt-1'></span>}
        </p>
    );

    return (
        <>
            {isExpanded ? (
                parentRow
            ) : (
                <Link to={OPS_MY_ACCOUNT_PARENT_PATH} onClick={() => onNav(ctx, props, 'userAssignment')}>
                    {parentRow}
                </Link>
            )}
            {isExpanded && isAccountOpen && (
                <div>
                    <Link to='/notifications'>
                        <p
                            className='font-b2 ms-3 cursor mt-2 border-bottom mb-2 pb-2'
                            onClick={() => onNav(ctx, props, 'notifications')}>
                            <i className='sf-chevron-right me-2'></i>Notifications
                        </p>
                    </Link>
                    <Link to='/userAssignment'>
                        <p
                            className='font-b2 ms-3 cursor mt-2 mb-2'
                            onClick={() => onNav(ctx, props, 'userAssignment')}>
                            <i className='sf-chevron-right me-2'></i>User Assignment
                        </p>
                    </Link>
                </div>
            )}
            <hr className='my-0' />
        </>
    );
};

export const SidebarInternalOpsNav = ({ ctx, props, showAssignment }) => {
    const location = useLocation();
    if (!showInternalOpsNav(ctx.userData)) {
        return null;
    }
    const activeKey = getOpsSidebarActiveKey(location.pathname, location.search);
    return (
        <>
            <OpsNavLink
                to='/dashboard?tab=transactions'
                page='allTransactions'
                iconClass='sf-all-transaction'
                label='All Transactions'
                isActive={activeKey === 'allTransactions'}
                ctx={ctx}
                props={props}
            />
            <OpsNavLink
                to='/dashboard?tab=requests'
                page='allRequests'
                iconClass='sf-my-requests'
                label='All Requests'
                isActive={activeKey === 'allRequests'}
                ctx={ctx}
                props={props}
            />
            <OpsNavLink
                to='/manageProfiles'
                page='profile'
                iconClass='sf-manage-profile'
                label='Manage Profile'
                isActive={activeKey === 'profile'}
                ctx={ctx}
                props={props}
            />
            <OpsNavLink
                to='/facilities'
                page='allFacilities'
                iconClass='sf-all-facilities'
                label='All Facilities'
                isActive={activeKey === 'allFacilities'}
                ctx={ctx}
                props={props}
            />
            {showPricingConfigurator(ctx.userData) && (
                <OpsNavLink
                    to='/pricingConfigurator'
                    page='pricingConfigurator'
                    iconClass='sf-pricing-conf'
                    label='Pricing Configurator'
                    isActive={activeKey === 'pricingConfigurator'}
                    ctx={ctx}
                    props={props}
                />
            )}
            {showOpsMyAccount(showAssignment) && (
                <OpsAccountSection ctx={ctx} props={props} isActive={activeKey === 'myAccount'} />
            )}
        </>
    );
};

const InvestorCreateNew = ({ ctx, props }) => {
    const platformRole = getPlatformRole(ctx.userData);
    const isOperator = getRoleRelationship(ctx.userData) === 'operator';
    if (!(ctx.userData && isOperator && platformRole !== 'investor')) {
        return null;
    }
    return (
        <>
            <p
                className={`sidebar-nav-item pt-2 pb-2 font-b2 mb-0 cursor border-top-gray mt-2 ${pageClass(
                    ctx.pageActive === 'facility' || ctx.pageActive === 'createProfile'
                )}`}
                {...collapsedNavAttrs(props.isBarExpanded, 'Create New')}>
                <span className='bi-person-plus home-icon'></span>
                <span className={props.isBarExpanded ? 'd-inline ' : 'd-none'}>Create New</span>
            </p>
            <div className={props.isBarExpanded ? 'd-inline' : 'd-none'}>
                <Link to='/facility'>
                    <p className='font-b2 ps-4 ms-2 cursor mt-2' onClick={() => onNav(ctx, props, 'facility')}>
                        <i className='sf-chevron-right me-2'></i>Facility
                    </p>
                </Link>
                <Link to='/createProfile'>
                    <p className='font-b2 ps-4 ms-2 cursor' onClick={() => onNav(ctx, props, 'createProfile')}>
                        <i className='sf-chevron-right me-2'></i>Profile
                    </p>
                </Link>
            </div>
        </>
    );
};

export const SidebarInvestorNav = ({ ctx, props, showAssignment }) => {
    const { isAccountOpen, setIsAccountOpen, isExpanded } = useAccountSubmenu(props.isBarExpanded);
    if (!ctx.userData || getPlatformRole(ctx.userData) !== 'investor') {
        return null;
    }
    const platformRole = getPlatformRole(ctx.userData);
    const accountRow = (
        <p
            className={`sidebar-nav-item pt-2 pb-2 font-b2 mb-0 cursor border-top-gray mt-2 ${pageClass(
                ctx.pageActive === 'notifications' || ctx.pageActive === 'myProfile'
            )}`}
            {...collapsedNavAttrs(props.isBarExpanded, 'My Account')}
            role={isExpanded ? 'button' : undefined}
            aria-expanded={isExpanded ? isAccountOpen : undefined}
            onClick={isExpanded ? () => setIsAccountOpen((open) => !open) : undefined}>
            <span className='sf-user home-icon'></span>
            <span className={props.isBarExpanded ? 'd-inline align-top' : 'd-none'}>My Account</span>
            {isExpanded && <span className='sf-chevron-down float-end me-3 mt-1 pt-1'></span>}
        </p>
    );
    return (
        <>
            {ctx.userData && platformRole !== 'investor' && (
                <Link to='/facilities'>
                    <p
                        className={`sidebar-nav-item pt-3 pb-2 font-b2 cursor mb-0 ${pageClass(ctx.pageActive === 'allFacilities')}`}
                        {...collapsedNavAttrs(props.isBarExpanded, 'All Facilities')}
                        onClick={() => onNav(ctx, props, 'allFacilities')}>
                        <i className=' home-icon sf-all-facilities'></i>
                        <span className={`align-top  ${props.isBarExpanded ? 'd-inline' : 'd-none'}`}>
                            All Facilities
                        </span>
                    </p>
                </Link>
            )}
            <Link to='/investmentHistory'>
                <p
                    className={`sidebar-nav-item pt-3 pb-2 font-b2 cursor mb-0 border-bottom ${pageClass(
                        ctx.pageActive === 'investmentHistory'
                    )}`}
                    {...collapsedNavAttrs(props.isBarExpanded, 'Investment History')}
                    onClick={() => onNav(ctx, props, 'investmentHistory')}>
                    <span className='sf-money home-icon'></span>
                    <span className={`align-top  ${props.isBarExpanded ? 'd-inline' : 'd-none'}`}>
                        Investment History
                    </span>
                </p>
            </Link>
            <InvestorCreateNew ctx={ctx} props={props} />
            {isExpanded ? (
                accountRow
            ) : (
                <Link to='/myProfile' onClick={() => onNav(ctx, props, 'myProfile')}>
                    {accountRow}
                </Link>
            )}
            {isExpanded && isAccountOpen && (
                <div>
                    <Link to='/notifications'>
                        <p
                            className='font-b2  ms-4 cursor mt-2 border-bottom mb-2 pb-2'
                            onClick={() => onNav(ctx, props, 'notifications')}>
                            <i className='sf-chevron-right me-2'></i>Notifications
                        </p>
                    </Link>
                    <Link to='/myProfile'>
                        <p
                            className={`font-b2  ms-4 cursor mt-2 ${showAssignment && 'border-bottom'} mb-2 pb-2`}
                            onClick={() => onNav(ctx, props, 'myProfile')}>
                            <i className='sf-chevron-right me-2'></i>My Profile
                        </p>
                    </Link>
                    {showAssignment && (
                        <Link to='/userAssignment'>
                            <p
                                className='font-b2  ms-4 cursor mt-2'
                                onClick={() => onNav(ctx, props, 'userAssignment')}>
                                <i className='sf-chevron-right me-2'></i>User Assignment
                            </p>
                        </Link>
                    )}
                </div>
            )}
            <hr className='my-0' />
        </>
    );
};
